import React, { useState, useEffect } from 'react';
import { ArrowUpRight, User, Tag, ArrowRight, ArrowDown } from 'lucide-react';
import caseImg1 from '../../../Assets/Images/Case1.jpg'
import caseImg2 from '../../../Assets/Images/Case2.jpg'
import caseImg3 from '../../../Assets/Images/Case3.jpg'
import caseImg4 from '../../../Assets/Images/Case4.jpg'
import caseImg5 from '../../../Assets/Images/Case5.webp'


export default function CaseStudies() {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedCase, setSelectedCase] = useState(0);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const caseStudies = [
    {
      id: 0,
      title: 'Developing a Scalable Growth Framework for Market Expansion',
      description: 'A structured growth framework was engineered to refine resource allocation, uncover high-value opportunities, and align execution with long-term business priorities. The outcome delivered operational clarity, improved efficiency, and consistent performance expansion.',
      client : 'SaaS Infrastructure',
      tags: 'Industry: Consumer Goods',
      img : caseImg1,
      featured: true
    },
    {
      id: 1,
      title: ' Establishing a Distinct Brand Positioning for a SaaS Venture',
      description: ' A precise brand positioning strategy was defined to differentiate the SaaS offering within a competitive landscape. This involved aligning messaging, visual identity, and market perception to strengthen brand recall, elevate credibility, and support long-term customer acquisition.',
      client: 'SaaS Startup',
      tags: 'Brand Strategy',
       img : caseImg2,
      featured: false
    },
    {
      id: 2,
      title: 'Enhancing Lead Acquisition for a Financial Services Organization',
      description: ' A performance-focused acquisition model was implemented to refine targeting, improve conversion pathways, and maximize lead quality. The approach resulted in a more efficient pipeline, reduced acquisition costs, and stronger alignment with revenue objectives.',
      client: 'Financial Services',
      tags: ' Conversion Optimization',
       img : caseImg3,
      featured: false
    },
    {
      id: 3,
      title: 'Accelerating Business Performance Through Intelligent Automation',
      description: ' Operational bottlenecks were identified and replaced with automated workflows designed to improve accuracy, reduce manual intervention, and increase execution speed. This transformation enabled consistent output quality and enhanced overall business productivity.',
      client: 'Enterprise Operations',
      tags: 'Process Automation',
       img : caseImg5,
      featured: false
    },
     {
      id: 4,
      title: 'Optimizing Operational Workflows Using Data-Driven Insights',
      description: ' Advanced data analysis was leveraged to uncover inefficiencies and restructure internal workflows. By integrating actionable insights into daily operations, the organization achieved improved decision-making, streamlined processes, and measurable performance gains.',
      client: 'Business Intelligence',
      tags: 'Data Analytics',
       img : caseImg4,
      featured: false
    },
  ];

  const selectedStudy = caseStudies[selectedCase];

  return (
    <div className=" bg-gradient-to-br from-slate-50 to-slate-100 py-8 lg:py-12 px-6">
      <div className="container mx-auto max-w-7xl">
        {/* Header Section */}
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16 mb-8 md:mb-16">
          {/* Left */}
          <div className="space-y-4 md:space-y-8">
            {/* Badge */}
            <div className='flex justify-center lg:justify-start'>
            <div 
              className={`inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-900 rounded-full bg-white transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
              }`}
            >
              <div className="w-2 h-2 bg-slate-900 rounded-sm"></div>
              <span className="text-slate-900 font-medium">Featured Case Studies</span>
            </div>
            </div>

            {/* Heading */}
            <div className="space-y-2 text-center lg:text-start">
              <h2 
                className={`text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight transition-all duration-1000 delay-200 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                 IT Solutions That Establish
              </h2>
              <h3 
                className={`text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight transition-all duration-1000 delay-300 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                Long-Term <span className="">Business Confidence.</span>
              </h3>
            </div>
          </div>

          {/* Right */}
          <div className="flex flex-col justify-center space-y-8">
            <p 
              className={`text-slate-600 text-justify md:text-lg leading-relaxed transition-all duration-1000 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Explore how our technology-driven initiatives strengthen operational integrity, ensure regulatory alignment, and enable organizations to scale with precision. Each engagement reflects measurable outcomes and sustainable business advancement.
            </p>

            <div 
              className={`transition-all duration-1000 delay-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <button className="group px-8 py-4 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 transition-all duration-300 hover:scale-105 hover:shadow-2xl flex items-center gap-3">
                Explore Case Portfolio
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>

        {/* Case Studies Section */}
        <div 
          className={`grid lg:grid-cols-2 gap-8 transition-all duration-1000 delay-900 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          {/* Left - Case List */}
          <div className="space-y-0 border-2 border-slate-200 rounded-3xl overflow-hidden bg-white">
            {caseStudies.map((study, index) => (
              <div
                key={study.id}
                onClick={() => setSelectedCase(index)}
                className={`p-3 md:p-6 cursor-pointer transition-all duration-300 border-b-2 border-slate-200 last:border-b-0 group ${
                  selectedCase === index
                    ? 'bg-slate-900 text-white'
                    : 'hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className={`font-bold  md:text-lg mb-2 transition-colors ${
                      selectedCase === index ? 'text-white' : 'text-slate-900 group-hover:text-slate-700'
                    }`}>
                      {study.title}
                    </h4>
                  </div>
                  <div className={`flex-shrink-0 ml-4 w-10 h-10 rounded-lg flex items-center justify-center transition-all group ${
                    selectedCase === index
                      ? 'bg-gradient-to-br from-purple-500 to-blue-500'
                      : 'bg-slate-100 group-hover:bg-slate-200'
                  }`}>
                    {selectedCase === index ? (
                      <div className='transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1'>
                      <ArrowRight className="w-5 h-5 text-white hidden lg:flex" />
                      <ArrowDown className="w-5 h-5 text-white flex lg:hidden transition-all duration-300" />
                      </div>
                    ) : (
                      <div className='transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1'>
                      <ArrowUpRight className="w-5 h-5 text-slate-600 hidden lg:flex" />
                      <ArrowRight className="w-5 h-5 text-slate-600 flex lg:hidden " />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right - Case Detail */}
          <div className="bg-white border-2 border-slate-200 rounded-3xl overflow-hidden">
            <div className="relative h-full flex flex-col">
              {/* Content */}
              <div className="p-8 space-y-6 flex-1">
                <h3 
                  className="text-2xl lg:text-3xl font-bold text-slate-900 transition-all duration-500"
                  key={`title-${selectedCase}`}
                >
                  {selectedStudy.title}
                </h3>
                
                <p 
                  className="text-slate-600 leading-relaxed transition-all duration-500"
                  key={`desc-${selectedCase}`}
                >
                  {selectedStudy.description}
                </p>
              </div>

              {/* Image Section */}
              <div className="relative h-72 bg-gradient-to-br from-slate-200 to-slate-300">
                <div className="">
                  <img src={selectedStudy.img} alt="" className='object-cover rounded-lg w-full' />
                </div>

                {/* Tags Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-white/95 to-transparent">
                  <div className="flex flex-wrap gap-3">
                    <div className="px-4 py-2 bg-white rounded-lg shadow-sm border border-slate-200 flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-600" />
                      <span className="text-sm font-medium text-slate-700">Client: {selectedStudy.client}</span>
                    </div>
                    <div className="px-4 py-2 bg-white rounded-lg shadow-sm border border-slate-200 flex items-center gap-2">
                      <Tag className="w-4 h-4 text-slate-600" />
                      <span className="text-sm font-medium text-slate-700">{selectedStudy.tags}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}